import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { useErrorReporter } from "@/components/error-reporter";
import { quotaStatus, quotaSetModelEnabled } from "@/lib/ai-gateway/admin.functions";
import { RefreshCw, ShieldAlert, Gauge, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/_authenticated/quota")({
  component: QuotaPage,
});

function Meter({ value, soft, cap }) {
  const pct = cap > 0 ? Math.min(100, (value / cap) * 100) : 0;
  const over = value > soft;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium tabular-nums">
          {value}
          <span className="text-muted">
            {" "}
            / {soft} soft · {cap} cap
          </span>
        </span>
        {over && <Badge variant="destructive">Over soft cap</Badge>}
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-2">
        <div
          className={`h-full rounded-full transition-all ${
            over ? "bg-destructive" : value >= soft * 0.8 ? "bg-warning" : "bg-accent"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function BudgetBar({ label, used, cap, warnAtPct }) {
  const pct = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
  const warn = pct >= warnAtPct;
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{label}</p>
        <span className="text-xs text-muted tabular-nums">
          {used} / {cap}
        </span>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-surface-2">
        <div
          className={`h-full rounded-full transition-all ${warn ? "bg-warning" : "bg-accent"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {warn && (
        <p className="mt-1.5 text-xs text-warning">At or above {warnAtPct}% — watch the budget.</p>
      )}
    </Card>
  );
}

function QuotaPage() {
  const { isSuper } = useAuth();
  const { reportError } = useErrorReporter();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const read = useServerFn(quotaStatus);
  const setEnabled = useServerFn(quotaSetModelEnabled);

  const load = useCallback(async () => {
    try {
      const data = await read();
      setStatus(data);
    } catch (e) {
      reportError(e, { area: "quota.status" });
    } finally {
      setLoading(false);
    }
  }, [read, reportError]);

  useEffect(() => {
    if (isSuper) load();
  }, [isSuper, load]);

  async function toggleModel(m) {
    setBusy(true);
    try {
      await setEnabled({ data: { model: m.id, enabled: m.disabled } });
      toast.success(m.disabled ? `Enabled ${m.id}` : `Disabled ${m.id}`);
      await load();
    } catch (e) {
      reportError(e, { area: "quota.setModelEnabled" });
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="min-h-screen bg-bg" aria-busy="true" />;

  if (!isSuper) {
    return (
      <div className="min-h-screen bg-bg">
        <div className="container mx-auto px-4 py-16 text-center">
          <ShieldAlert className="mx-auto size-12 text-destructive" />
          <h1 className="mt-4 text-3xl font-bold">Superadmin only</h1>
          <p className="text-muted mt-2">You need superadmin privileges to view AI quota.</p>
          <Button
            asChild
            className="mt-6 bg-gradient-primary text-accent-foreground shadow-elegant"
          >
            <Link to="/superadmin">Back to superadmin</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg">
      <div className="container mx-auto max-w-5xl px-4 pb-12">
        <main className="mt-8 space-y-8">
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted">
                {status?.day ?? "—"} · {status?.tier ?? ""} tier
              </p>
              <h1 className="mt-1 text-3xl font-light tracking-tight sm:text-4xl">AI quota</h1>
              <p className="mt-1 text-sm text-muted">
                Gemini free-tier usage per Google Cloud project per model.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to="/superadmin">
                  <ArrowLeft className="size-4" /> Superadmin
                </Link>
              </Button>
              <Button variant="outline" size="sm" onClick={() => load()} disabled={busy}>
                <RefreshCw className={`size-4 ${busy ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
          </header>

          <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Card className="p-4">
              <p className="text-sm font-medium">Next reset</p>
              <p className="mt-1 text-lg font-light tabular-nums">
                {status?.nextResetKigali ?? "—"}
              </p>
              <p className="text-xs text-muted tabular-nums">
                {status?.nextResetUtc ? `${status.nextResetUtc} UTC` : ""}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-sm font-medium">Tokens today</p>
              <p className="mt-1 text-lg font-light tabular-nums">
                {(status?.tokensIn ?? 0).toLocaleString()} in
              </p>
              <p className="text-xs text-muted tabular-nums">
                {(status?.tokensOut ?? 0).toLocaleString()} out
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-sm font-medium">Pending marking</p>
              <p className="mt-1 text-lg font-light tabular-nums">{status?.pendingMarking ?? 0}</p>
              <p className="text-xs text-muted">
                Submissions awaiting AI marking (none today — grading is deterministic).
              </p>
            </Card>
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Gauge className="size-4 text-muted" />
              <h2 className="text-lg font-medium">Models</h2>
            </div>
            {status?.models?.length ? (
              status.models.map((m) => (
                <Card
                  key={m.id}
                  className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm">{m.id}</span>
                      <Badge variant="outline">priority {m.priority}</Badge>
                      {m.disabled && <Badge variant="secondary">Disabled</Badge>}
                      {m.exhausted && <Badge variant="destructive">Exhausted today</Badge>}
                    </div>
                    <Meter value={m.usedRpd} soft={m.softRpd} cap={m.rpd} />
                  </div>
                  <Button
                    variant={m.disabled ? "outline" : "secondary"}
                    size="sm"
                    onClick={() => toggleModel(m)}
                    disabled={busy}
                    className="shrink-0"
                  >
                    {m.disabled ? "Enable" : "Disable"}
                  </Button>
                </Card>
              ))
            ) : (
              <Card className="p-6 text-sm text-muted">
                No models are configured. Set <code className="font-mono">GEMINI_MODELS</code>.
              </Card>
            )}
          </section>

          <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <BudgetBar
              label="Daily generation budget"
              used={status?.budget?.gen?.used ?? 0}
              cap={status?.budget?.gen?.cap ?? 0}
              warnAtPct={status?.budget?.warnAtPct ?? 80}
            />
            <BudgetBar
              label="Daily marking budget"
              used={status?.budget?.mark?.used ?? 0}
              cap={status?.budget?.mark?.cap ?? 0}
              warnAtPct={status?.budget?.warnAtPct ?? 80}
            />
          </section>
        </main>
      </div>
    </div>
  );
}
